import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import type Highcharts from "highcharts";

// Keeps every Highcharts widget reflowed to its grid cell. The PDF export no longer
// captures charts here: the server renderer draws them from the saved spec.

type ChartEntry = {
  getCell: () => Element | null;
  getChart: () => Highcharts.Chart | null;
};

type ChartSyncContextValue = {
  register: (id: string, getCell: () => Element | null, getChart: () => Highcharts.Chart | null) => void;
  unregister: (id: string) => void;
  requestSync: () => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
};

const ChartSyncContext = createContext<ChartSyncContextValue | null>(null);

const RESIZE_DEBOUNCE_MS = 300;

export function ChartSyncProvider({
  children,
  containerRef,
}: {
  children: ReactNode;
  containerRef: React.RefObject<HTMLDivElement | null>;
}) {
  const registryRef = useRef(new Map<string, ChartEntry>());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chartSizesRef = useRef(new Map<string, string>());

  const register = useCallback(
    (id: string, getCell: () => Element | null, getChart: () => Highcharts.Chart | null) => {
      registryRef.current.set(id, { getCell, getChart });
    },
    []
  );

  const unregister = useCallback((id: string) => {
    registryRef.current.delete(id);
    chartSizesRef.current.delete(id);
  }, []);

  const syncAllChartsRef = useRef<() => void>(() => {});

  const syncAllCharts = useCallback(() => {
    const entries = Array.from(registryRef.current.entries());

    for (const [id, { getCell, getChart }] of entries) {
      const cell = getCell();
      const chart = getChart();
      if (!cell || !chart?.container) continue;

      // Only reflow charts whose container size actually changed
      const { clientWidth: w, clientHeight: h } = cell;
      const sizeKey = `${w}x${h}`;
      if (chartSizesRef.current.get(id) === sizeKey) continue;
      chartSizesRef.current.set(id, sizeKey);
      chart.reflow();
    }
  }, []);

  syncAllChartsRef.current = syncAllCharts;

  const requestSync = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      requestAnimationFrame(() => syncAllChartsRef.current());
    }, RESIZE_DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const runSync = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        requestAnimationFrame(syncAllCharts);
      }, RESIZE_DEBOUNCE_MS);
    };

    const ro = new ResizeObserver(runSync);
    ro.observe(el);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      ro.disconnect();
    };
  }, [containerRef, syncAllCharts]);

  const value: ChartSyncContextValue = {
    register,
    unregister,
    requestSync,
    containerRef,
  };

  return (
    <ChartSyncContext.Provider value={value}>
      {children}
    </ChartSyncContext.Provider>
  );
}

export function useChartSync() {
  return useContext(ChartSyncContext);
}
